import { useState, useDeferredValue } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { Add, Search, CloudUpload } from "@mui/icons-material";
import { useArticles } from "@/hooks/useKB";
import { KB_CATEGORY_LABELS, type KBCategory, type KBStatus } from "@/types";

const STATUS_COLORS: Record<KBStatus, "success" | "warning" | "default"> = {
  published: "success",
  draft: "warning",
  archived: "default",
};

export default function KBList() {
  const navigate = useNavigate();
  const [category, setCategory] = useState<KBCategory | "">("");
  const [statusFilter, setStatusFilter] = useState<KBStatus | "">("");
  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search);

  const {
    data: articles,
    isLoading,
    error,
  } = useArticles({
    category: category || undefined,
    status: statusFilter || undefined,
    search: deferredSearch || undefined,
  });

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <Typography variant="h4" sx={{ flex: 1 }}>
          Knowledge Base
        </Typography>
        <Button
          variant="outlined"
          startIcon={<CloudUpload />}
          onClick={() => navigate("/kb/ingest")}
        >
          Import Files
        </Button>
        <Button variant="contained" startIcon={<Add />} onClick={() => navigate("/kb/new")}>
          New Article
        </Button>
      </Box>

      {/* Filters */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <TextField
          size="small"
          placeholder="Search articles..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <Search sx={{ mr: 1, color: "text.secondary" }} /> }}
          sx={{ minWidth: 250 }}
        />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Category</InputLabel>
          <Select
            value={category}
            label="Category"
            onChange={(e) => setCategory(e.target.value as KBCategory | "")}
          >
            <MenuItem value="">All Categories</MenuItem>
            {Object.entries(KB_CATEGORY_LABELS).map(([key, label]) => (
              <MenuItem key={key} value={key}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => setStatusFilter(e.target.value as KBStatus | "")}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="draft">Draft</MenuItem>
            <MenuItem value="published">Published</MenuItem>
            <MenuItem value="archived">Archived</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {deferredSearch && (
        <Chip
          label="Semantic + keyword search active"
          size="small"
          color="primary"
          variant="outlined"
          sx={{ mb: 2 }}
        />
      )}

      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && <Alert severity="error">Failed to load articles.</Alert>}

      {articles && articles.length === 0 && (
        <Alert severity="info">No articles found. Create your first article to get started.</Alert>
      )}

      {/* Article list */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {articles?.map((article) => (
          <Card key={article.id} sx={{ "&:hover": { boxShadow: 3 } }}>
            <CardActionArea onClick={() => navigate(`/kb/${article.id}`)}>
              <CardContent sx={{ display: "flex", alignItems: "center", gap: 2, py: 1.5 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {article.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {KB_CATEGORY_LABELS[article.category]} · v{article.version} · Updated{" "}
                    {new Date(article.updated_at).toLocaleDateString()}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                  {article.tags?.map((tag) => (
                    <Chip key={tag} label={tag} size="small" variant="outlined" />
                  ))}
                </Box>
                <Chip label={article.status} size="small" color={STATUS_COLORS[article.status]} />
              </CardContent>
            </CardActionArea>
          </Card>
        ))}
      </Box>
    </Box>
  );
}
